import { useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router";

import { Box, Typography, useMediaQuery } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { BUDGET_STATUS_ACTIVE, BUDGET_STATUS_DRAFT } from "components/Budget/helpers";
import TilleyDataGrid from "components/shared/DataGrid/TilleyDataGrid";
import DeleteConfirmationButton from "mui-toolkit/DeleteConfirmationButton";
import { formatNumber, formatPrice } from "utility/formatters";

import { CustomWidthTooltip } from "../../../mui-toolkit/CustomWidthTooltip";
import { NavigateIcon } from "../../Icons/Icons";

export default function InsuranceSummaryDataTable({
  insuranceScenarios,
  isLoading,
  onChange,
  pageSizes = [5, 10, 25, 50, 100],
  rowHeight = 80,
}) {
  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("lg"), { noSsr: true });

  const toggleStatus = useCallback(
    (currentInsuranceScenario) => {
      const _newState = { ...currentInsuranceScenario };
      _newState.status =
        currentInsuranceScenario.status === BUDGET_STATUS_ACTIVE ? BUDGET_STATUS_DRAFT : BUDGET_STATUS_ACTIVE;
      onChange(_newState);
    },
    [onChange]
  );

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 1.5,
        renderCell: (params) => (
          <Typography color="primary" component={Link} to={`/insurance/${params.row.id}`}>
            {params.row.name}
          </Typography>
        ),
      },
      {
        field: "producer_premium_amount_per_acre",
        headerName: "Producer",
        flex: 1,
        renderCell: (params) => {
          return formatPrice(params.row.totals?.producer_premium_amount_per_acre, 2);
        },
      },
      {
        field: "reported_acreage",
        headerName: "Coverage",
        flex: 1,
        renderCell: (params) => {
          return formatPrice(params.row.totals?.liability_amount_per_acre, 0);
        },
      },
      {
        field: "status",
        headerName: "Status",
        flex: 1,
        display: "flex",
        renderCell: (params) => {
          const isActive = params.row.status === BUDGET_STATUS_ACTIVE;

          return (
            <Box
              onClick={() => {
                if (!isLoading) {
                  toggleStatus(params.row);
                }
              }}
              sx={{
                backgroundColor: isActive ? "primary.main" : "transparent",
                color: isActive ? "secondary.contrastText" : "text.secondary",
                padding: "8px 24px",
                border: "2px solid",
                borderColor: isActive ? "primary.main" : "text.secondary",
                borderRadius: "999px",
                fontWeight: 600,
                cursor: isLoading ? "not-allowed" : "pointer",
                opacity: isLoading ? 0.7 : 1,
                "&:hover": {
                  backgroundColor: isActive ? (isLoading ? "primary.main" : "primary.dark") : "transparent",
                  borderColor: isActive ? (isLoading ? "primary.main" : "primary.dark") : "text.secondary",
                },
              }}
            >
              {isActive ? "Active" : "Draft"}
            </Box>
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        type: "actions",
        headerAlign: "right",
        align: "right",
        width: 100,
        sortable: false,
        getActions: (params) => {
          return [
            <CustomWidthTooltip key="delete" title="Delete" placement="top" arrow>
              <DeleteConfirmationButton item={params.row} onChange={onChange} />
            </CustomWidthTooltip>,
            <GridActionsCellItem
              key="view"
              icon={<NavigateIcon />}
              label="View Details"
              data-testid={`Open Budget ${params.row.name}`}
              onClick={() => navigate(`/insurance/${params.row.id}`)}
            />,
          ];
        },
        flex: 0,
      },
    ],
    [navigate, formatNumber, formatPrice, isLoading, toggleStatus]
  );

  return (
    <>
      <TilleyDataGrid
        rows={insuranceScenarios || []}
        columns={columns}
        pageSizeOptions={pageSizes}
        defaultPageSize={isMobile ? 5 : 10}
        gridKey="insurance-summary"
        getRowId={(row) => row.id}
        rowHeight={rowHeight}
        isLoading={isLoading}
        sx={{ width: "99%", boxSizing: "border-box" }}
      />
    </>
  );
}
